import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './RechargePage.module.css';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useSystemLogin } from '../contexts/SystemLoginContext';
import config from '../config';
import { fetchWithAuth } from '../utils/api';
import i18n from '../i18n';

interface ShopAiPackageBalance {
  id: number;
  shopId: number;
  version: number;
  aiPackageType: number;
  aiPackageName: string;
  createdTime: string;
  lastUpdatedTime: string;
  rechargeTime: string;
  renewTime: string;
  renewCount: number;
  universalTokenAmount: number;
  packageMonthName: string;
}

interface ShopAiPackageDefine {
  id: number;
  aiPackageType: number;
  aiPackageName: string;
  createdTime: string;
  lastUpdatedTime: string;
  universalTokenAmount: number;
  positionId: number;
  goodsId: number;
  price: number;
  currencyId: number;
  currencyCode: string;
  currencyName: string;
}

interface BalanceData {
  universalToken: {
    amount: number;
  };
}

const RechargePage: React.FC = () => {
  const navigate = useNavigate();
  const systemLogin = useSystemLogin();
  
  // 使用 ref 来防止重复调用
  const initializationAttempted = useRef(false);
  const isFetchingBalance = useRef(false);
  const isFetchingPackages = useRef(false);
  const [loading, setLoading] = useState(false);
  const [balance, setBalance] = useState<BalanceData | null>(null);
  const [amount, setAmount] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [universalTokenPackages, setUniversalTokenPackages] = useState<ShopAiPackageDefine[]>([]);
  const [selectedPackage, setSelectedPackage] = useState<ShopAiPackageDefine | null>(null);

  const fetchBalance = useCallback(async () => {
    if (isFetchingBalance.current) {
      console.log(`正在获取数据中，跳过重复请求`);
      return;
    }

    isFetchingBalance.current = true;
    setError(null);
    try {
      const response = await fetchWithAuth(
        `${config.API_BASE_URL}/app-api/deductionReport/getShopAiPackageBalance`, 
        {
          method: 'GET',
          headers: {
            'Accept': 'application/json, text/plain, */*',
            'Accept-Language': i18n.language,
            'Origin': config.APP_BASE_URL,
            'Referer': `${config.APP_BASE_URL}/`
          }
        },
        navigate
      );

      if (response.code === 1 && response.data) {
        const balanceData = response.data as ShopAiPackageBalance;
        setBalance({
          universalToken: {
            amount: balanceData.universalTokenAmount
          }
        });
      } else {
        setError(response.msg || '获取余额失败');
      }
    } catch (error) {
      console.error('Error fetching balance:', error);
      if (!(error instanceof Error && error.message === 'Login required')) {
        setError('获取余额失败');
      }
    } finally {
      isFetchingBalance.current = false;
    }
  }, [navigate]);

  const fetchUniversalTokenPackages = useCallback(async () => {
    if (isFetchingPackages.current) {
      console.log(`正在获取数据中，跳过重复请求`);
      return;
    }
    
    isFetchingPackages.current = true;
    try {
      const response = await fetchWithAuth(
        `${config.API_BASE_URL}/app-api/deductionReport/getAllShopAiPackageDefine`, 
        {
          method: 'GET',
          headers: {
            'Accept': 'application/json, text/plain, */*',
            'Accept-Language': i18n.language,
            'Origin': config.APP_BASE_URL,
            'Referer': `${config.APP_BASE_URL}/`
          }
        },
        navigate
      );

      if (response.code === 1 && Array.isArray(response.data)) {
        // 只保留通用令牌套餐 (aiPackageType = 21)
        const universalPackages = response.data.filter(
          (pkg: ShopAiPackageDefine) => pkg.aiPackageType === 21
        );
        setUniversalTokenPackages(universalPackages);
      } else {
        setError(response.msg || '获取套餐失败');
      }
    } catch (error) {
      console.error('Error fetching packages:', error);
      if (!(error instanceof Error && error.message === 'Login required')) {
        setError('获取套餐失败');
      }
    } finally {
      isFetchingPackages.current = false;
    }
  }, [navigate]);

  useEffect(() => {
    if (systemLogin.isLoggedIn && !initializationAttempted.current) {
      initializationAttempted.current = true;
      try {
        if (!loading) {
          setLoading(true);
          fetchBalance();
          fetchUniversalTokenPackages();
        }
      } finally {
        setLoading(false);
      }
    }
  }, [fetchBalance, fetchUniversalTokenPackages, systemLogin.isLoggedIn, loading]);

  const handleBack = () => {
    navigate('/', { replace: true });
  };

  // 获取币种符号
  const getCurrencySymbol = (currencyCode: string): string => {
    const currencySymbols: { [key: string]: string } = {
      'CNY': '¥',
      'USD': '$',
      'EUR': '€',
      'GBP': '£',
      'JPY': '¥',
      'KRW': '₩',
      'HKD': 'HK$',
      'SGD': 'S$',
      'CAD': 'C$',
      'AUD': 'A$',
      'CHF': 'CHF',
      'SEK': 'kr',
      'NOK': 'kr',
      'DKK': 'kr',
      'RUB': '₽',
      'INR': '₹',
      'BRL': 'R$',
      'MXN': '$',
      'ZAR': 'R',
      'TRY': '₺',
      'THB': '฿',
      'MYR': 'RM',
      'IDR': 'Rp',
      'PHP': '₱',
      'VND': '₫'
    };
    
    return currencySymbols[currencyCode] || currencyCode;
  };

  const handlePackageSelect = (pkg: ShopAiPackageDefine) => {
    setSelectedPackage(pkg);
    setAmount((pkg.price / 100).toFixed(2)); // Convert cents to yuan
  };

  const handleRecharge = async () => {
    if (!amount || !selectedPackage) {
      setError('请选择套餐');
      return;
    }

    const numAmount = Number(amount);
    if (numAmount <= 0) {
      setError('请输入有效金额');
      return;
    }

    setLoading(true);
    try {
      const data = await fetchWithAuth(
        `${config.API_BASE_URL}/app-api/pay/create/v1`, 
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            amountString: numAmount.toFixed(2),
            currency: selectedPackage?.currencyCode || "CNY",
            returnUrl: `${window.location.origin}/recharge-result`,
            rechargeOrderType: 6,
            goodsId: selectedPackage?.goodsId
          })
        },
        navigate
      );

      if (data && data.data?.payData) {
        try {
          const payDataUrl = new URL(data.data.payData);
          const codeParam = payDataUrl.searchParams.get('code');
          
          if (codeParam) {
            navigate(`/recharge-confirm?code=${codeParam}&amount=${numAmount}&currency=${selectedPackage?.currencyCode || "CNY"}`);
          } else {
            setError('支付参数错误');
          }
        } catch (urlError) {
          console.error('Error parsing payData URL:', urlError);
          setError('支付链接解析失败');
        }
      } else {
        setError('充值失败');
      }
    } catch (error) {
      console.error('Error creating recharge:', error);
      if (!(error instanceof Error && error.message === 'Login required')) {
        setError('充值失败');
      }
    } finally {
      setLoading(false);
    }
  };

  if (!systemLogin.isLoggedIn) {
    return (
      <div className={styles.container}>
        <Header />
        <div className={styles.content}>
          <div className={styles.loginPrompt}>
            <h2>请先登录</h2>
            <p>登录后即可查看生成图片余额和进行充值</p>
            <button onClick={() => navigate('/login')} className={styles.loginButton}>
              前往登录
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <header className={styles.header}>
          <button onClick={handleBack} className={styles.backButton}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" className={styles.backIcon}>
              <path d="M19 12H5M12 19l-7-7 7-7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            返回
          </button>
          <h1 className={styles.title}>图片生成充值</h1>
        </header>

        {loading && (
          <div className={styles.loadingOverlay}>
            <div className={styles.loadingSpinner}></div>
            <span>加载中...</span>
          </div>
        )}

        <main className={styles.main}>
          {/* Balance Display */}
          {balance && (
            <div className={styles.balanceDisplay}>
              <div className={styles.balanceTitle}>剩余图片生成次数</div>
              <div className={styles.balanceNumber}>{balance.universalToken.amount}</div>
            </div>
          )}

          {/* Package Selection */}
          <div className={styles.packageSection}>
            <div className={styles.sectionTitle}>选择充值套餐</div>
            <div className={styles.packageGrid}>
              {universalTokenPackages.map((pkg) => (
                <div
                  key={pkg.id}
                  className={`${styles.packageCard} ${selectedPackage?.id === pkg.id ? styles.selected : ''}`}
                  onClick={() => handlePackageSelect(pkg)}
                >
                  <div className={styles.packageName}>{pkg.aiPackageName}</div>
                  <div className={styles.packageTokens}>
                    <div className={styles.tokenItem}>
                      <span className={styles.tokenLabel}>图片生成次数</span>
                      <span className={styles.tokenValue}>{pkg.universalTokenAmount}</span>
                    </div>
                  </div>
                  <div className={styles.packagePrice}>
                    {getCurrencySymbol(pkg.currencyCode)} {(pkg.price / 100).toFixed(2)}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {error && <div className={styles.errorMessage}>{error}</div>}

          {/* Recharge Button */}
          <button 
            className={styles.rechargeButton} 
            onClick={handleRecharge}
            disabled={!selectedPackage || loading}
          >
            {loading ? (
              <>
                <span className={styles.loadingSpinner}></span>
                处理中...
              </>
            ) : (
              '立即充值'
            )}
          </button>

          {/* Recharge History Button */}
          <button 
            className={styles.historyButton} 
            onClick={() => navigate('/recharge-history')}
          >
            充值记录
          </button>
        </main>
      </div>
      
      <Footer />
    </div>
  );
};

export default RechargePage;